import { getMetricUnitByKey } from '@utils/metrics/metricHelpers';
import { LESS_THAN_NUMBER } from '@utils/metrics';

type HumanizedResult = {
  humanizedNumber: string;
  humanizedUnit: string;
};

const humanizeUtil = (number: number, metricKey: string, metricUnit?: string): HumanizedResult => {
  const formatDecimal = (val: number): string => {
    if (val % 1 === 0) return val.toString();
    const decimals = Math.abs(val) < 1 ? 3 : 2;
    return val.toFixed(decimals).replace(/\.?0+$/, '');
  };

  const humanize = (value: number, unit: string): string => {
    if (typeof value !== 'number' || isNaN(value)) return 'NaN';

    // Percent
    if (unit === '%') {
      const absValue = Math.abs(value);

      // Values below 1000% render directly with precision-aware decimals.
      if (absValue < 1000) {
        const decimals = absValue < 1 ? 3 : 2;
        return `${value.toFixed(decimals).replace(/\.?0+$/, '')} %`;
      }

      // Values >= 1000% scale with K/M/B/T (e.g. period-over-period growth metrics).
      const pctPrefixes = ['', 'K', 'M', 'B', 'T'];
      let pctIndex = 0;
      let scaled = absValue;
      while (scaled >= 1000 && pctIndex < pctPrefixes.length - 1) {
        scaled /= 1000;
        pctIndex++;
      }
      if (scaled >= 1000) {
        return `${Number(value).toExponential(1)} %`;
      }
      const signed = value < 0 ? -scaled : scaled;
      return `${formatDecimal(signed)} ${pctPrefixes[pctIndex]} %`;
    }

    // Time-based humanization
    if (unit === 'ms') {
      if (value < 1000) return `${formatDecimal(value)} ms`;
      const seconds = value / 1000;
      if (seconds < 60) return `${formatDecimal(seconds)} sec`;
      const minutes = seconds / 60;
      if (minutes < 60) return `${formatDecimal(minutes)} min`;
      const hours = minutes / 60;
      if (hours < 24) return `${formatDecimal(hours)} hrs`;
      const days = hours / 24;
      if (days < 30) return `${formatDecimal(days)} day`;
      const months = days / 30;
      return `${formatDecimal(months)} month`;
    }

    // Generic number scaling (up to T = 10^12, beyond that falls back to scientific notation)
    const prefixes = unit === 'bps' ? ['', 'K', 'M', 'G', 'T'] : ['', 'K', 'M', 'B', 'T'];

    let index = 0;
    let absValue = Math.abs(value);

    while (absValue >= 1000 && index < prefixes.length - 1) {
      absValue /= 1000;
      index++;
    }

    // Fallback to scientific notation for extremely large numbers (≥ 10^15)
    if (absValue >= 1000) {
      return Number(value).toExponential(1);
    }

    return `${formatDecimal(absValue)} ${prefixes[index]}`;
  };

  const unit = metricUnit ?? getMetricUnitByKey({ metricKey });

  if (number == null || isNaN(number)) {
    return { humanizedNumber: 'N/A', humanizedUnit: unit || '' };
  }

  if (number < LESS_THAN_NUMBER && number > 0) {
    return { humanizedNumber: '< 0.001', humanizedUnit: unit || '' };
  }

  const humanizedValue = humanize(number, unit);

  // eslint-disable-next-line no-useless-escape
  const match = humanizedValue.match(/([\d\.\-e+<]+)\s*(\w+)?/);

  if (!match) {
    return { humanizedNumber: humanizedValue, humanizedUnit: unit || '' };
  }

  const [, humanizedNumber, extractedUnit] = match;
  const noSpace = unit === 'bps';

  let finalUnit = extractedUnit;

  if (unit === 'ms' && extractedUnit && extractedUnit !== 'ms') {
    finalUnit = extractedUnit;
  } else if (unit === 'bps' && extractedUnit) {
    finalUnit = `${extractedUnit}${noSpace ? '' : ' '}${unit}`;
  } else if (unit === '%' && extractedUnit && extractedUnit !== '%') {
    // Large percent values pick up a K/M/B/T prefix from the inner scaling
    // (e.g. "1.5 M %" for 1_500_000%); preserve it.
    finalUnit = `${extractedUnit} %`;
  } else if (unit === '%' || extractedUnit === '%') {
    finalUnit = '%';
  } else if (unit && extractedUnit && unit !== extractedUnit) {
    finalUnit = `${extractedUnit} ${unit}`;
  }

  return {
    humanizedNumber,
    humanizedUnit: finalUnit || unit || '',
  };
};

export function humanize(value: any, unit?: any): string | null {
  let prefix;

  if (typeof value !== 'number') {
    return null;
  }

  const num = value.toExponential(2).split('e');
  const mant = Math.round(Number(num[0]) * 100);
  const exp = Number(num[1]);

  let res: any = (mant * 10 ** (exp % 3)) / 100;

  if (unit === 'ms') {
    const replaceableUnit = ['ms', 'secs', 'mins'];
    res += ` ${replaceableUnit[Math.floor(exp / 3)]}`;
    return res;
  }
  const isBPSUnit = unit === 'bps';

  if (isBPSUnit) {
    prefix = ['', 'K', 'M', 'G', 'T', 'P', 'E', 'Z', 'Y'];
  } else {
    prefix = ['', 'K', 'M', 'B', 'T', 'P', 'E', 'Z', 'Y'];
  }

  if (exp >= -3 && exp < 0) {
    res = Math.round(Number(value) * 1000) / 1000;
  } else if (exp >= 0 && exp < 3) {
    res += isBPSUnit ? ' ' : '';
  } else if (exp > 0 && exp < 24) {
    res += ` ${prefix[Math.floor(exp / 3)]}`;
  } else {
    res = Number(value).toExponential(1);
  }

  return res;
}

export const formatEndedStatus = (data: string): string => {
  switch (data) {
    case 'eGracefulEnd':
      return 'Graceful End';
    case 'eByExpiration':
      return 'By Expiration';
    case 'eNotEnded':
      return 'Not Ended';
    default:
      return '';
  }
};

export default humanizeUtil;
